import React, { useCallback, useRef, useState } from "react";
import api, { getApiErrorMessage } from "../../core/api";
import Icon from "../../components/Icon/Icon";
import { ConsensoPrivacy } from "../../components/Contratto/Contratto";
import { API_URL, APP_NAME } from "../../config/app";
import s from "./Login.module.scss";

// La registrazione crea l'utente e il suo studio, con 30 giorni di prova
// (limiti del piano Professional), senza carta. Poi serve un piano a pagamento.
// Due passi: account (con la presa visione dell'Informativa Privacy) e codice
// via email. Il contratto non si firma qui ma all'attivazione del piano, alla
// fine della prova (Gestione abbonamento), come su gestionale-gls. Chi arriva
// da Google ha l'email già verificata: solo il nome dello studio.
const STEPS = ["Account", "Verifica email"];
const INDICE = { account: 0, codice: 1 };
const PRIVACY_MSG = "Per continuare conferma di aver letto l'Informativa Privacy.";

export default function RegisterForm({ onBack, onSuccess, googleProfile }) {
  const [step, setStep] = useState(googleProfile ? "google" : "account");
  const [form, setForm] = useState({
    nome: googleProfile?.nome || "",
    cognome: googleProfile?.cognome || "",
    ragioneSociale: "",
    email: googleProfile?.email || "",
    password: "",
    confirm: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [cooldown, setCooldown] = useState(0);
  const [verificationToken, setVerificationToken] = useState(null);
  const refs = useRef([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (extra) => {
    if (!form.ragioneSociale.trim()) return setError("Inserisci il nome dello studio.");
    if (!privacy) return setError(PRIVACY_MSG);
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/register", {
        nomeCompleto: `${form.nome} ${form.cognome}`.trim(),
        email: form.email.trim(),
        ragioneSociale: form.ragioneSociale.trim(),
        consensoPrivacy: privacy,
        ...extra,
      });
      await onSuccess(res.data);
    } catch (err) {
      setLoading(false);
      setError(getApiErrorMessage(err, "Registrazione non riuscita. Riprova."));
    }
    return undefined;
  };

  const sendCode = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/send-verification", { email: form.email.trim() });
      setCooldown(60);
      const t = setInterval(() => setCooldown((c) => (c <= 1 ? (clearInterval(t), 0) : c - 1)), 1000);
    } catch (err) {
      setError(getApiErrorMessage(err, "Invio del codice non riuscito. Riprova."));
    } finally {
      setLoading(false);
    }
  }, [form.email]);

  const continueAccount = async (e) => {
    e.preventDefault();
    const { nome, cognome, ragioneSociale, email, password, confirm } = form;
    if (!nome.trim() || !cognome.trim()) return setError("Inserisci nome e cognome.");
    if (!ragioneSociale.trim()) return setError("Inserisci il nome dello studio.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError("Inserisci un'email valida.");
    if (password.length < 8) return setError("La password deve contenere almeno 8 caratteri.");
    if (password !== confirm) return setError("Le password non coincidono.");
    if (!privacy) return setError(PRIVACY_MSG);
    setError("");
    try {
      setLoading(true);
      await api.post("/auth/check-email", { email: email.trim() });
    } catch (err) {
      setLoading(false);
      if (err?.response?.status === 409) return setError("Questa email è già associata a un account: accedi.");
      return setError(getApiErrorMessage(err, "Controllo dell'email non riuscito."));
    }
    setLoading(false);
    setStep("codice");
    // Il codice si manda una volta sola: tornando indietro e avanti non se ne chiede un altro.
    if (!cooldown && !verificationToken) await sendCode();
    return undefined;
  };

  const verify = async () => {
    if (verificationToken) return create({ password: form.password, emailVerificationToken: verificationToken });
    const code = otp.join("");
    if (code.length !== 6) return setError("Inserisci il codice a 6 cifre.");
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/verify-email", { email: form.email.trim(), code });
      setVerificationToken(res.data.verificationToken);
      return create({ password: form.password, emailVerificationToken: res.data.verificationToken });
    } catch (err) {
      setLoading(false);
      return setError(getApiErrorMessage(err, "Codice non valido. Riprova."));
    }
  };

  const onOtp = (i, v) => {
    if (v && !/^\d$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    setOtp(Array.from({ length: 6 }, (_, i) => digits[i] || ""));
    refs.current[Math.min(digits.length, 5)]?.focus();
  };

  const goTo = (next) => {
    setStep(next);
    setError("");
  };

  const consensoPrivacy = <ConsensoPrivacy className={s.consent} checked={privacy} onChange={setPrivacy} />;

  return (
    <div className={s.wizard}>
      {!googleProfile && (
        <div className={s.stepper}>
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={s.stepItem}>
                <div className={`${s.stepCircle} ${i <= INDICE[step] ? s.stepOn : ""}`}>{i < INDICE[step] ? <Icon name="check" size={14} strokeWidth={3} /> : i + 1}</div>
                <span className={`${s.stepLabel} ${i === INDICE[step] ? s.stepLabelOn : ""}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`${s.stepLine} ${i < INDICE[step] ? s.stepLineOn : ""}`} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {step === "account" && (
        <form className={s.formBox} onSubmit={continueAccount}>
          <h2 className={s.welcome}>Crea il tuo studio</h2>
          <p className={s.subtitle}>Presenze, dipendenti e scadenze dei vostri clienti</p>
          <div className={s.freeNote}>
            <strong>30 giorni di prova completa, gratis.</strong>
            <span>Nessun metodo di pagamento richiesto: alla fine della prova scegli il piano in base ai dipendenti dei tuoi clienti e accetti il contratto. Utenti illimitati.</span>
          </div>
          <div className={s.form}>
            <div className={s.row}>
              <div className={s.inputWrap}>
                <input placeholder="Nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} autoComplete="given-name" />
              </div>
              <div className={s.inputWrap}>
                <input placeholder="Cognome" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} autoComplete="family-name" />
              </div>
            </div>
            <div className={s.inputWrap}>
              <Icon name="building" size={18} />
              <input placeholder="Nome dello studio" value={form.ragioneSociale} onChange={(e) => set("ragioneSociale", e.target.value)} autoComplete="organization" />
            </div>
            <div className={s.inputWrap}>
              <Icon name="mail" size={18} />
              <input type="email" placeholder="Email di lavoro" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
            </div>
            <div className={s.inputWrap}>
              <Icon name="lock" size={18} />
              <input type={showPw ? "text" : "password"} placeholder="Password (almeno 8 caratteri)" value={form.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" />
              <button type="button" className={s.eye} onClick={() => setShowPw((v) => !v)} aria-label="Mostra password">
                <Icon name={showPw ? "eyeOff" : "eye"} size={18} />
              </button>
            </div>
            <div className={s.inputWrap}>
              <Icon name="lock" size={18} />
              <input type={showPw ? "text" : "password"} placeholder="Ripeti la password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} autoComplete="new-password" />
            </div>
            {consensoPrivacy}
            {error && <div className="pr-error">{error}</div>}
            <button type="submit" className={s.mainButton} disabled={loading}>
              {loading ? "Attendi..." : "Continua"}
            </button>
          </div>
          <p className={s.prompt}>
            Hai già un account?{" "}
            <button type="button" className="pr-link-btn" onClick={onBack}>
              Accedi
            </button>
          </p>
        </form>
      )}

      {step === "google" && (
        <div className={s.formBox}>
          <h2 className={s.welcome}>Il tuo studio</h2>
          <p className={s.subtitle}>Come si chiama lo studio di {googleProfile.email}?</p>
          <div className={s.form}>
            <div className={s.inputWrap}>
              <Icon name="building" size={18} />
              <input placeholder="Nome dello studio" value={form.ragioneSociale} onChange={(e) => set("ragioneSociale", e.target.value)} autoFocus />
            </div>
            {consensoPrivacy}
            {error && <div className="pr-error">{error}</div>}
            <button type="button" className={s.mainButton} disabled={loading} onClick={() => create({ googleAccessToken: googleProfile.accessToken })}>
              {loading ? "Creazione in corso..." : "Crea il mio studio"}
            </button>
          </div>
          <button type="button" className={s.back} onClick={onBack}>
            <Icon name="arrowLeft" size={16} /> Indietro
          </button>
        </div>
      )}

      {step === "codice" && (
        <div className={s.formBox}>
          <span className={s.mailIcon}>
            <Icon name="mail" size={34} strokeWidth={1.5} />
          </span>
          <h2 className={s.welcome}>Conferma la tua email</h2>
          <p className={s.subtitle}>
            Abbiamo inviato un codice a 6 cifre a<br />
            <strong className={s.accent}>{form.email}</strong>
          </p>
          <div className={s.otp} onPaste={onPaste}>
            {otp.map((d, i) => (
              <input
                key={i}
                ref={(el) => (refs.current[i] = el)}
                className={`${s.otpCell} ${d ? s.otpFilled : ""}`}
                inputMode="numeric"
                maxLength={1}
                value={d}
                autoFocus={i === 0}
                onChange={(e) => onOtp(i, e.target.value)}
                onKeyDown={(e) => e.key === "Backspace" && !otp[i] && i > 0 && refs.current[i - 1]?.focus()}
              />
            ))}
          </div>
          {process.env.NODE_ENV !== "production" && (
            <a className={s.devHint} href={`${API_URL}/dev/outbox`} target="_blank" rel="noopener noreferrer">
              <Icon name="info" size={16} /> Modalità offline: il codice è nella posta in uscita
            </a>
          )}
          {error && <div className="pr-error">{error}</div>}
          <button type="button" className={s.mainButton} onClick={verify} disabled={loading || otp.join("").length !== 6}>
            {loading ? "Verifica in corso..." : `Verifica e crea ${APP_NAME}`}
          </button>
          <p className={s.prompt}>
            Non hai ricevuto il codice?{" "}
            <button type="button" className="pr-link-btn" disabled={cooldown > 0 || loading} onClick={sendCode}>
              {cooldown > 0 ? `Reinvia tra ${cooldown}s` : "Reinvia codice"}
            </button>
          </p>
          <button type="button" className={s.back} onClick={() => goTo("account")}>
            <Icon name="arrowLeft" size={16} /> Indietro
          </button>
        </div>
      )}
    </div>
  );
}
